import * as React from "react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import { Callout } from "../../../ui/components/Callout";
import { Checkbox } from "../../../ui/components/Checkbox";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import {
  fetchSmartLinkImportEnvironments,
  previewSmartLinkFullSync,
  commitSmartLinkFullSync,
} from "../../lib/api/smart-link";
import type {
  SmartLinkImportEnvironment,
  SmartLinkFullSyncPreviewItem,
} from "../../lib/api/smart-link";
import * as s from "./SmartLinkFullSyncModal.css";

export interface SmartLinkFullSyncModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onCommitted: () => void;
}

type Step = "pick-environment" | "reviewing" | "done";

/**
 * Bulk backfill: pulls every Smart user Pro doesn't have a claim for yet.
 * Idempotent (creates rows, and only ever replaces an unlinked claim whose
 * Smart user no longer exists) and always preview-then-confirm — nothing is written until the admin reviews
 * the exact list and confirms. Deliberately never auto-links a claim to an
 * existing Pro account, even when the email matches (flagged informationally
 * per row instead) — linking still requires that person to prove their
 * Smart password on login, same bar as the existing onboarding flow.
 */
export function SmartLinkFullSyncModal({ open, setOpen, onCommitted }: SmartLinkFullSyncModalProps) {
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const [environments, setEnvironments] = React.useState<SmartLinkImportEnvironment[]>([]);
  const [environment, setEnvironment] = React.useState<"primary" | "alt">("primary");
  const [step, setStep] = React.useState<Step>("pick-environment");
  const [loadingPreview, setLoadingPreview] = React.useState(false);
  const [items, setItems] = React.useState<SmartLinkFullSyncPreviewItem[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [error, setError] = React.useState<string | null>(null);
  const [committing, setCommitting] = React.useState(false);
  const [result, setResult] = React.useState<{ created: number; replaced: number; skipped: number } | null>(null);
  const selectable = React.useMemo(() => items.filter((i) => i.kind !== "conflict"), [items]);

  React.useEffect(() => {
    if (!open) {
      setStep("pick-environment");
      setItems([]);
      setSelectedIds(new Set());
      setError(null);
      setResult(null);
      return;
    }
    if (!token) return;
    fetchSmartLinkImportEnvironments(token)
      .then((envs) => {
        setEnvironments(envs);
        if (envs[0]) setEnvironment(envs[0].id);
      })
      .catch(() => setEnvironments([]));
  }, [open, token]);

  const handleLookup = async () => {
    if (!token) return;
    setLoadingPreview(true);
    setError(null);
    try {
      const preview = await previewSmartLinkFullSync(token, environment);
      setItems(preview.items);
      setSelectedIds(
        new Set(preview.items.filter((i) => i.kind !== "conflict").map((i) => i.smartUserId)),
      );
      setStep("reviewing");
    } catch (err: any) {
      const errorCode = err?.data?.error ?? "";
      if (errorCode === "smart_link_api_not_configured") {
        setError(t("pro.system.smartLink.import.error.notConfigured"));
      } else if (errorCode === "smart_link_api_unavailable" || err?.status === 503) {
        setError(t("pro.system.smartLink.import.error.unavailable"));
      } else {
        setError(err?.message || t("pro.system.smartLink.fullSync.error.lookup"));
      }
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleCommit = async () => {
    if (!token) return;
    setCommitting(true);
    setError(null);
    try {
      const res = await commitSmartLinkFullSync(token, environment, [...selectedIds]);
      setResult({ created: res.created, replaced: res.replaced, skipped: res.skipped });
      setStep("done");
      onCommitted();
    } catch (err: any) {
      setError(err?.message || t("pro.system.smartLink.fullSync.error.commit"));
    } finally {
      setCommitting(false);
    }
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(selectable.map((i) => i.smartUserId)) : new Set());
  };

  const toggleOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      minWidth="520px"
      maxWidth="640px"
      maxHeight="75vh"
      closeOnClickOff={!committing}
    >
      <Modal.Header>
        <Text level="h2" weight="bold" className={s.title}>
          {t("pro.system.smartLink.fullSync.title")}
        </Text>
        <p className={s.subtitle}>{t("pro.system.smartLink.fullSync.subtitle")}</p>
      </Modal.Header>

      <Modal.Body>
        {step === "pick-environment" && (
          <div className={s.fields}>
            <div className={s.field}>
              <label className={formStyles.label}>{t("pro.system.smartLink.import.environment")}</label>
              <select
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as "primary" | "alt")}
                disabled={loadingPreview || environments.length === 0}
                className={formStyles.input}
              >
                {environments.length === 0 && (
                  <option value="primary">{t("pro.system.smartLink.import.noEnvironments")}</option>
                )}
                {environments.map((env) => (
                  <option key={env.id} value={env.id}>
                    {env.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {step === "reviewing" &&
          (items.length === 0 ? (
            <Callout variant="info">{t("pro.system.smartLink.fullSync.nothingToImport")}</Callout>
          ) : (
            <>
              <div className={s.reviewHeader}>
                <Checkbox
                  checked={selectable.length > 0 && selectedIds.size === selectable.length}
                  indeterminate={selectedIds.size > 0 && selectedIds.size < selectable.length}
                  disabled={selectable.length === 0}
                  onCheckedChange={toggleAll}
                  aria-label={t("pro.system.smartLink.fullSync.selectAll")}
                />
                <Text level="body" weight="medium">
                  {t("pro.system.smartLink.fullSync.selectedCount", {
                    selected: selectedIds.size,
                    total: selectable.length,
                  })}
                </Text>
              </div>
              <div className={s.rowList}>
                {items.map((item) => (
                  <div key={item.smartUserId} className={s.row}>
                    <Checkbox
                      checked={selectedIds.has(item.smartUserId)}
                      disabled={item.kind === "conflict"}
                      onCheckedChange={(checked) => toggleOne(item.smartUserId, checked)}
                      aria-label={item.email}
                    />
                    <div className={s.rowInfo}>
                      <p className={s.rowEmail}>{item.email}</p>
                      <p className={s.rowName}>
                        {[item.firstName, item.lastName].filter(Boolean).join(" ") || "—"}
                      </p>
                    </div>
                    {item.kind === "replace" && (
                      <span className={s.replaceBadge} title={t("pro.system.smartLink.fullSync.replaceTooltip")}>
                        {t("pro.system.smartLink.fullSync.replaceBadge")}
                      </span>
                    )}
                    {item.kind === "conflict" && (
                      <span
                        className={s.conflictBadge}
                        title={t(
                          item.existingClaim?.linked
                            ? "pro.system.smartLink.fullSync.conflictTooltipLinked"
                            : "pro.system.smartLink.fullSync.conflictTooltip",
                        )}
                      >
                        {t("pro.system.smartLink.fullSync.conflictBadge")}
                      </span>
                    )}
                    {item.existingProUserMatch && (
                      <span className={s.matchBadge} title={t("pro.system.smartLink.fullSync.matchTooltip")}>
                        {t("pro.system.smartLink.fullSync.matchBadge")}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </>
          ))}

        {step === "done" && result && (
          <Callout variant="success">
            {t("pro.system.smartLink.fullSync.doneMessage", {
              created: result.created,
              replaced: result.replaced,
              skipped: result.skipped,
            })}
          </Callout>
        )}

        {error && (
          <div className={s.errorWrap}>
            <Callout variant="error">{error}</Callout>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {step === "pick-environment" && (
          <>
            <Button type="button" color="neutral" onClick={() => setOpen(false)}>
              {t("pro.system.smartLink.import.cancel")}
            </Button>
            <Button
              type="button"
              color="primary"
              loading={loadingPreview}
              disabled={loadingPreview || environments.length === 0}
              onClick={() => void handleLookup()}
            >
              {t("pro.system.smartLink.fullSync.lookup")}
            </Button>
          </>
        )}
        {step === "reviewing" && (
          <>
            <Button
              type="button"
              color="neutral"
              onClick={() => setStep("pick-environment")}
              disabled={committing}
            >
              {t("pro.system.smartLink.fullSync.back")}
            </Button>
            {items.length > 0 && (
              <Button
                type="button"
                color="primary"
                loading={committing}
                disabled={committing || selectedIds.size === 0}
                onClick={() => void handleCommit()}
              >
                {t("pro.system.smartLink.fullSync.confirmImport", { count: selectedIds.size })}
              </Button>
            )}
          </>
        )}
        {step === "done" && (
          <Button type="button" color="primary" onClick={() => setOpen(false)}>
            {t("shared.common.close")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
